import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import * as fx from "../domain/fixtures";
import { buildInitialSituations } from "../domain/situationEngine";
import { inspectionSuggestedAction } from "../domain/jarvis";
import { canTransition, coiMachine, moveOutMachine, supportRequestMachine, tourMachine, workOrderMachine } from "../domain/stateMachines";
import type {
  Activity,
  COI,
  COIStatus,
  GeneratedAction,
  Inspection,
  InspectionItemResult,
  Lease,
  Message,
  MoveInPlan,
  MoveOutPlan,
  Receivable,
  Site,
  SupportRequest,
  SupportRequestStatus,
  SyncQueueItem,
  Tenant,
  Tour,
  TourOutcomeKind,
  Unit,
  Vendor,
  WorkOrder,
  WorkOrderStatus
} from "../domain/types";

const STORE_VERSION = 1;

interface DemoState {
  dmName: string;
  activeSiteId: string;
  isOnline: boolean;
}

interface AppState {
  // canonical entities (mutable copies of the fixtures)
  sites: Site[];
  units: Unit[];
  tenants: Tenant[];
  leases: Lease[];
  receivables: Receivable[];
  workOrders: WorkOrder[];
  vendors: Vendor[];
  tours: Tour[];
  supportRequests: SupportRequest[];
  cois: COI[];
  moveInPlans: MoveInPlan[];
  moveOutPlans: MoveOutPlan[];
  messages: Message[];
  activities: Activity[];
  inspections: Inspection[];
  generatedActions: GeneratedAction[];

  // FieldFlow-native layer
  situations: ReturnType<typeof buildInitialSituations>;

  // demo/reviewer state
  demo: DemoState;
  syncQueue: SyncQueueItem[];

  // ---- derived helpers (selectors kept here for convenience) ----
  situationsForActiveSite: () => AppState["situations"];
  unitsForActiveSite: () => Unit[];

  // ---- actions ----
  switchSite: (siteId: string) => void;
  toggleOnline: () => void;
  resetDemo: () => void;

  resolveSituation: (situationId: string) => void;
  deferSituation: (situationId: string) => void;
  recordMessage: (unitId: string, channel: Message["channel"], body: string, draftedByJarvis: boolean) => void;

  advanceWorkOrder: (workOrderId: string, to: WorkOrderStatus, note?: string) => void;
  verifyWorkOrder: (workOrderId: string, result: "pass" | "fail") => void;

  advanceTour: (tourId: string, to: Tour["status"]) => void;
  recordTourOutcome: (tourId: string, kind: TourOutcomeKind, mainObjection: string | null, nextStep: string) => void;
  startMoveInFromTour: (tourId: string, targetMoveInDate: string) => void;

  updateMoveInItem: (planId: string, key: string, done: boolean) => void;

  advanceMoveOut: (planId: string, to: MoveOutPlan["stage"]) => void;

  advanceCoi: (coiId: string, to: COIStatus) => void;

  injectSupportRequest: (unitId: string) => void;
  advanceSupportRequest: (requestId: string, to: SupportRequestStatus) => void;

  startInspection: (inspectionId: string) => void;
  pauseInspection: (inspectionId: string) => void;
  recordInspectionItem: (
    inspectionId: string,
    itemId: string,
    result: InspectionItemResult,
    extras?: { note?: string; photoId?: string; voiceTranscript?: string }
  ) => void;
  setInspectionStep: (inspectionId: string, index: number) => void;
  completeInspection: (inspectionId: string) => void;
  finishInspectionReview: (inspectionId: string) => void;
  reviewGeneratedAction: (actionId: string, decision: "accepted" | "edited" | "discarded", editedText?: { title: string; description: string }) => void;

  enqueueSync: (item: Omit<SyncQueueItem, "id" | "queuedAt" | "status">) => void;
  drainSyncQueue: () => void;
}

function uid(prefix: string) {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

function nowIso() {
  return new Date().toISOString().slice(0, 19);
}

function buildInitialEntityState() {
  return {
    sites: fx.sites,
    units: fx.units,
    tenants: fx.tenants,
    leases: fx.leases,
    receivables: fx.receivables,
    workOrders: fx.workOrders,
    vendors: fx.vendors,
    tours: fx.tours,
    supportRequests: fx.supportRequests,
    cois: fx.cois,
    moveInPlans: fx.moveInPlans,
    moveOutPlans: fx.moveOutPlans,
    messages: fx.messages,
    activities: fx.activities,
    inspections: fx.inspectionsSeed,
    generatedActions: fx.generatedActionsSeed
  };
}

function buildInitialState() {
  const entities = buildInitialEntityState();
  const situations = buildInitialSituations(entities);
  return {
    ...entities,
    situations,
    demo: { dmName: "Morgan Ortiz", activeSiteId: "site-fishers", isOnline: true },
    syncQueue: [] as SyncQueueItem[]
  };
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      ...buildInitialState(),

      situationsForActiveSite: () => get().situations.filter((s) => s.siteId === get().demo.activeSiteId),
      unitsForActiveSite: () => get().units.filter((u) => u.siteId === get().demo.activeSiteId),

      switchSite: (siteId) => set((state) => ({ demo: { ...state.demo, activeSiteId: siteId } })),

      toggleOnline: () => {
        const goingOnline = !get().demo.isOnline;
        set((state) => ({ demo: { ...state.demo, isOnline: goingOnline } }));
        if (goingOnline) get().drainSyncQueue();
      },

      resetDemo: () => set(() => buildInitialState()),

      resolveSituation: (situationId) =>
        set((state) => ({
          situations: state.situations.map((s) => (s.id === situationId ? { ...s, status: "resolved", resolvedAt: nowIso(), needsAttention: false } : s))
        })),

      deferSituation: (situationId) =>
        set((state) => ({
          situations: state.situations.map((s) => (s.id === situationId ? { ...s, status: "deferred", needsAttention: false } : s))
        })),

      recordMessage: (unitId, channel, body, draftedByJarvis) => {
        const message: Message = { id: uid("msg"), unitId, channel, direction: "outbound", body, at: nowIso(), draftedByJarvis };
        set((state) => ({ messages: [...state.messages, message] }));
        get().enqueueSync({ kind: "message", summary: `${channel === "email" ? "Email" : channel === "text" ? "Text" : channel === "call" ? "Call log" : "Note"} to unit ${unitId}` });
      },

      advanceWorkOrder: (workOrderId, to, note) =>
        set((state) => ({
          workOrders: state.workOrders.map((wo) => {
            if (wo.id !== workOrderId) return wo;
            if (!canTransition(workOrderMachine, wo.status, to)) return wo;
            return { ...wo, status: to, history: [...wo.history, { status: to, at: nowIso(), note }] };
          }),
          situations: syncWorkOrderSituations(state, workOrderId, to)
        })),

      verifyWorkOrder: (workOrderId, result) => {
        const to: WorkOrderStatus = result === "pass" ? "verified" : "in-progress";
        set((state) => ({
          workOrders: state.workOrders.map((wo) =>
            wo.id === workOrderId
              ? {
                  ...wo,
                  status: to,
                  verifiedAt: result === "pass" ? nowIso() : null,
                  verificationResult: result,
                  history: [...wo.history, { status: to, at: nowIso(), note: result === "pass" ? "Verified by DM." : "Failed verification — sent back for repair." }]
                }
              : wo
          ),
          situations: syncWorkOrderSituations(state, workOrderId, to, result)
        }));
        get().enqueueSync({ kind: "workorder-update", summary: `Work order ${workOrderId} verification: ${result}` });
      },

      advanceTour: (tourId, to) =>
        set((state) => ({
          tours: state.tours.map((t) => (t.id === tourId && canTransition(tourMachine, t.status, to) ? { ...t, status: to } : t)),
          situations: syncTourSituations(state, tourId, to)
        })),

      recordTourOutcome: (tourId, kind, mainObjection, nextStep) => {
        const to = kind === "closed-won" ? "closed-won" : kind === "not-a-fit" ? "closed-lost" : "follow-up";
        set((state) => ({
          tours: state.tours.map((t) =>
            t.id === tourId
              ? { ...t, status: to as Tour["status"], outcome: { kind, mainObjection, nextStep, recordedAt: nowIso() } }
              : t
          ),
          situations: syncTourSituations(state, tourId, to as Tour["status"])
        }));
      },

      startMoveInFromTour: (tourId, targetMoveInDate) => {
        const tour = get().tours.find((t) => t.id === tourId);
        if (!tour) return;
        const unit = get().units.find((u) => u.id === tour.unitId);
        if (!unit) return;
        const tenantId = uid("tenant");
        const tenant: Tenant = {
          id: tenantId,
          companyName: tour.prospectCompany,
          contactName: tour.prospectContact,
          contactEmail: `${tour.prospectContact.split(" ")[0]?.toLowerCase() ?? "contact"}@${tour.prospectCompany.replace(/\s+/g, "").toLowerCase()}.com`,
          contactPhone: "(317) 555-0190",
          tenantSince: targetMoveInDate
        };
        const plan: MoveInPlan = {
          id: uid("movein"),
          unitId: unit.id,
          tenantId,
          targetMoveInDate,
          status: "in-progress",
          items: [
            { key: "unit-inspection", label: "Unit inspection", done: false },
            { key: "interior-photos", label: "Interior condition photos", done: false },
            { key: "exterior-photos", label: "Exterior condition photos", done: false },
            { key: "access-setup", label: "Access fobs + door codes issued", done: false },
            { key: "internet-service", label: "Internet / service activation confirmed", done: false },
            { key: "signage", label: "Suite signage installed", done: false },
            { key: "welcome-confirmation", label: "Tenant welcome + move-in confirmation sent", done: false }
          ]
        };
        set((state) => ({
          tenants: [...state.tenants, tenant],
          units: state.units.map((u) => (u.id === unit.id ? { ...u, tenantId, status: "leased-pending-move-in" } : u)),
          moveInPlans: [...state.moveInPlans, plan],
          situations: [
            ...state.situations,
            {
              id: `sit-${plan.id}`,
              siteId: unit.siteId,
              unitId: unit.id,
              form: "tracked" as const,
              category: "move-in" as const,
              priority: "high" as const,
              title: `Move-in readiness — ${unit.unitNumber}`,
              summary: `0 of ${plan.items.length} readiness items complete · target ${targetMoveInDate}`,
              sources: [{ kind: "move-in-plan" as const, id: plan.id, label: "New lease — move-in plan created" }],
              jarvis: [],
              workflow: { machine: "move-in", steps: moveInStepStatusLocal(plan) },
              scheduledAt: null,
              status: "open" as const,
              owner: "dm" as const,
              createdAt: nowIso(),
              resolvedAt: null,
              needsAttention: true,
              linkedMoveInPlanId: plan.id
            }
          ]
        }));
      },

      updateMoveInItem: (planId, key, done) =>
        set((state) => {
          const moveInPlans = state.moveInPlans.map((p) => {
            if (p.id !== planId) return p;
            const items = p.items.map((i) => (i.key === key ? { ...i, done } : i));
            const allDone = items.every((i) => i.done);
            return { ...p, items, status: allDone ? ("ready" as const) : p.status };
          });
          const plan = moveInPlans.find((p) => p.id === planId)!;
          const openCount = plan.items.filter((i) => !i.done).length;
          const situations = state.situations.map((s) =>
            s.linkedMoveInPlanId === planId
              ? {
                  ...s,
                  summary: `${plan.items.length - openCount}/${plan.items.length} readiness items complete · target ${plan.targetMoveInDate}`,
                  needsAttention: openCount > 0,
                  workflow: { machine: "move-in", steps: moveInStepStatusLocal(plan) }
                }
              : s
          );
          return { moveInPlans, situations };
        }),

      advanceMoveOut: (planId, to) =>
        set((state) => ({
          moveOutPlans: state.moveOutPlans.map((p) => (p.id === planId && canTransition(moveOutMachine, p.stage, to) ? { ...p, stage: to } : p)),
          situations: state.situations.map((s) =>
            s.linkedMoveOutPlanId === planId
              ? {
                  ...s,
                  summary: `Stage: ${to.replace(/-/g, " ")}`,
                  status: to === "ready" ? "resolved" : s.status,
                  workflow: { machine: "move-out", steps: moveOutStepStatusLocal(to) }
                }
              : s
          )
        })),

      advanceCoi: (coiId, to) =>
        set((state) => ({
          cois: state.cois.map((c) => (c.id === coiId && canTransition(coiMachine, c.status, to) ? { ...c, status: to } : c)),
          situations: state.situations.map((s) =>
            s.linkedCoiId === coiId
              ? { ...s, status: to === "verified" ? "resolved" : s.status, workflow: { machine: "coi", steps: coiStepStatusLocal(to) } }
              : s
          )
        })),

      injectSupportRequest: (unitId) => {
        const unit = get().units.find((u) => u.id === unitId);
        if (!unit) return;
        const request: SupportRequest = {
          id: uid("support"),
          unitId,
          submittedBy: "myrise-tenant",
          subject: "Loading dock light out",
          body: "The light above our loading dock has been out for two days — it's a safety issue after dark for our drivers.",
          status: "submitted",
          submittedAt: nowIso(),
          workOrderId: null
        };
        set((state) => ({
          supportRequests: [...state.supportRequests, request],
          situations: [
            ...state.situations,
            {
              id: `sit-${request.id}`,
              siteId: unit.siteId,
              unitId: unit.id,
              form: "tracked" as const,
              category: "support" as const,
              priority: "high" as const,
              title: `Support — ${request.subject}`,
              summary: request.body,
              sources: [{ kind: "support-request" as const, id: request.id, label: "Submitted via MyRise" }],
              jarvis: [],
              workflow: { machine: "support-request", steps: supportRequestStepStatusLocal("submitted") },
              scheduledAt: null,
              status: "open" as const,
              owner: "dm" as const,
              createdAt: nowIso(),
              resolvedAt: null,
              needsAttention: true,
              linkedSupportRequestId: request.id
            }
          ]
        }));
      },

      advanceSupportRequest: (requestId, to) => {
        if (!canTransition(supportRequestMachine, get().supportRequests.find((r) => r.id === requestId)?.status ?? "submitted", to)) return;
        set((state) => ({
          supportRequests: state.supportRequests.map((r) => (r.id === requestId ? { ...r, status: to } : r)),
          situations: state.situations.map((s) =>
            s.linkedSupportRequestId === requestId
              ? { ...s, status: to === "resolved" ? "resolved" : s.status, workflow: { machine: "support-request", steps: supportRequestStepStatusLocal(to) } }
              : s
          )
        }));
      },

      startInspection: (inspectionId) =>
        set((state) => ({
          inspections: state.inspections.map((i) => (i.id === inspectionId ? { ...i, status: "in-progress", startedAt: i.startedAt ?? nowIso() } : i))
        })),

      pauseInspection: (inspectionId) =>
        set((state) => ({ inspections: state.inspections.map((i) => (i.id === inspectionId ? { ...i, status: "paused" } : i)) })),

      recordInspectionItem: (inspectionId, itemId, result, extras) => {
        set((state) => ({
          inspections: state.inspections.map((insp) => {
            if (insp.id !== inspectionId) return insp;
            return {
              ...insp,
              status: "in-progress",
              items: insp.items.map((item) =>
                item.id === itemId
                  ? {
                      ...item,
                      result,
                      note: extras?.note ?? item.note,
                      photoIds: extras?.photoId ? [...item.photoIds, extras.photoId] : item.photoIds,
                      voiceTranscript: extras?.voiceTranscript ?? item.voiceTranscript
                    }
                  : item
              )
            };
          })
        }));
        const insp = get().inspections.find((i) => i.id === inspectionId);
        const item = insp?.items.find((i) => i.id === itemId);
        if (insp && item && (result === "attention" || result === "fail")) {
          const draft = inspectionSuggestedAction(item.title, item.category, result);
          const action: GeneratedAction = {
            id: uid("action"),
            sourceInspectionItemId: itemId,
            siteId: insp.siteId,
            unitId: insp.unitId,
            title: draft.title,
            description: draft.description,
            severity: result,
            status: "draft",
            createdWorkOrderId: null
          };
          set((state) => ({
            generatedActions: [...state.generatedActions, action],
            inspections: state.inspections.map((i) =>
              i.id === inspectionId ? { ...i, items: i.items.map((it) => (it.id === itemId ? { ...it, generatedActionId: action.id } : it)) } : i
            )
          }));
        }
        get().enqueueSync({ kind: "inspection-item", summary: `Inspection item recorded: ${item?.title ?? itemId} (${result ?? "cleared"})` });
      },

      setInspectionStep: (inspectionId, index) =>
        set((state) => ({ inspections: state.inspections.map((i) => (i.id === inspectionId ? { ...i, currentStepIndex: index } : i)) })),

      completeInspection: (inspectionId) =>
        set((state) => ({ inspections: state.inspections.map((i) => (i.id === inspectionId ? { ...i, status: "review" } : i)) })),

      finishInspectionReview: (inspectionId) =>
        set((state) => ({ inspections: state.inspections.map((i) => (i.id === inspectionId ? { ...i, status: "completed", completedAt: nowIso() } : i)) })),

      reviewGeneratedAction: (actionId, decision, editedText) => {
        set((state) => ({
          generatedActions: state.generatedActions.map((a) =>
            a.id === actionId ? { ...a, status: decision, ...(editedText ?? {}) } : a
          )
        }));
        const action = get().generatedActions.find((a) => a.id === actionId);
        if (action && decision !== "discarded") {
          const workOrder: WorkOrder = {
            id: uid("wo"),
            unitId: action.unitId ?? "unit-unknown",
            title: action.title,
            description: action.description,
            status: "reported",
            vendorId: null,
            reportedAt: nowIso(),
            repairedAt: null,
            verifiedAt: null,
            verificationResult: null,
            history: [{ status: "reported", at: nowIso(), note: "Generated from guided inspection." }]
          };
          set((state) => ({
            workOrders: [...state.workOrders, workOrder],
            generatedActions: state.generatedActions.map((a) => (a.id === actionId ? { ...a, createdWorkOrderId: workOrder.id } : a)),
            situations: [
              ...state.situations,
              {
                id: `sit-${workOrder.id}`,
                siteId: action.siteId,
                unitId: action.unitId,
                form: "tracked" as const,
                category: "inspection-followup" as const,
                priority: action.severity === "fail" ? "high" : ("medium" as const),
                title: workOrder.title,
                summary: workOrder.description,
                sources: [{ kind: "inspection-item" as const, id: action.sourceInspectionItemId, label: "Generated from guided inspection" }],
                jarvis: [],
                workflow: { machine: "work-order", steps: [{ key: "reported", label: "Reported", done: false, current: true }, { key: "assigned", label: "Assigned", done: false, current: false }, { key: "in-progress", label: "In Progress", done: false, current: false }, { key: "ready-to-verify", label: "Ready to Verify", done: false, current: false }, { key: "verified", label: "Verified", done: false, current: false }] },
                scheduledAt: null,
                status: "open" as const,
                owner: "dm" as const,
                createdAt: nowIso(),
                resolvedAt: null,
                needsAttention: action.severity === "fail",
                linkedWorkOrderId: workOrder.id
              }
            ]
          }));
        }
      },

      enqueueSync: (item) => {
        const entry: SyncQueueItem = { id: uid("sync"), queuedAt: nowIso(), status: get().demo.isOnline ? "syncing" : "queued", ...item };
        set((state) => ({ syncQueue: [...state.syncQueue, entry] }));
        if (get().demo.isOnline) {
          setTimeout(() => {
            set((state) => ({ syncQueue: state.syncQueue.map((s) => (s.id === entry.id ? { ...s, status: "synced" } : s)) }));
          }, 900);
        }
      },

      drainSyncQueue: () => {
        const queued = get().syncQueue.filter((s) => s.status === "queued");
        if (queued.length === 0) return;
        set((state) => ({ syncQueue: state.syncQueue.map((s) => (s.status === "queued" ? { ...s, status: "syncing" } : s)) }));
        setTimeout(() => {
          set((state) => ({ syncQueue: state.syncQueue.map((s) => (s.status === "syncing" ? { ...s, status: "synced" } : s)) }));
        }, 1100);
      }
    }),
    {
      name: "fieldflow-ff01-demo-state",
      version: STORE_VERSION,
      // Explicit rather than the default (which resolves the ambient global
      // `localStorage`): Node 22+ ships an experimental global `localStorage`
      // that shadows jsdom's `window.localStorage` in tests and silently
      // fails to persist, so we pin this to the DOM's localStorage.
      storage: createJSONStorage(() => window.localStorage)
    }
  )
);

// ---- local pure helpers (kept file-private; mirrors used by situationEngine) ----
function moveInStepStatusLocal(plan: MoveInPlan) {
  const states: Array<"lease-signed" | "readiness" | "walkthrough" | "ready" | "moved-in"> = ["lease-signed", "readiness", "walkthrough", "ready", "moved-in"];
  const labels = { "lease-signed": "Lease Signed", readiness: "Readiness", walkthrough: "Walkthrough", ready: "Ready", "moved-in": "Move-In" };
  const current = plan.status === "moved-in" ? "moved-in" : plan.status === "ready" ? "walkthrough" : "readiness";
  const idx = states.indexOf(current as (typeof states)[number]);
  return states.map((s, i) => ({ key: s, label: labels[s], done: i < idx, current: i === idx }));
}
function moveOutStepStatusLocal(current: MoveOutPlan["stage"]) {
  const states: MoveOutPlan["stage"][] = ["notice", "pre-walk", "move-out-inspection", "turnover-actions", "ready"];
  const labels: Record<MoveOutPlan["stage"], string> = { notice: "Notice", "pre-walk": "Pre-Walk", "move-out-inspection": "Move-Out Inspection", "turnover-actions": "Turnover Actions", ready: "Ready" };
  const idx = states.indexOf(current);
  return states.map((s, i) => ({ key: s, label: labels[s], done: i < idx, current: i === idx }));
}
function coiStepStatusLocal(current: COIStatus) {
  const states: COIStatus[] = ["flagged", "notified", "received", "verified"];
  const labels: Record<COIStatus, string> = { flagged: "Flagged", notified: "Notified", received: "Received", verified: "Verified" };
  const idx = states.indexOf(current);
  return states.map((s, i) => ({ key: s, label: labels[s], done: i < idx, current: i === idx }));
}
function supportRequestStepStatusLocal(current: SupportRequestStatus) {
  const states: SupportRequestStatus[] = ["submitted", "acknowledged", "in-progress", "waiting", "resolved"];
  const labels: Record<SupportRequestStatus, string> = { submitted: "Submitted", acknowledged: "Acknowledged", "in-progress": "In Progress", waiting: "Waiting", resolved: "Resolved" };
  const idx = states.indexOf(current);
  return states.map((s, i) => ({ key: s, label: labels[s], done: i < idx, current: i === idx }));
}
function workOrderStepStatusLocal(current: WorkOrderStatus) {
  const states: WorkOrderStatus[] = ["reported", "assigned", "in-progress", "ready-to-verify", "verified", "closed"];
  const labels: Record<WorkOrderStatus, string> = { reported: "Reported", assigned: "Assigned", "in-progress": "In Progress", "ready-to-verify": "Ready to Verify", verified: "Verified", closed: "Closed" };
  const idx = states.indexOf(current);
  return states.map((s, i) => ({ key: s, label: labels[s], done: i < idx, current: i === idx }));
}
function tourStepStatusLocal(current: Tour["status"]) {
  const states: Tour["status"][] = ["scheduled", "prep", "touring", "outcome-recorded", "follow-up", "closed-won", "closed-lost"];
  const labels: Record<Tour["status"], string> = { scheduled: "Scheduled", prep: "Prep", touring: "Tour", "outcome-recorded": "Outcome", "follow-up": "Follow-Up", "closed-won": "Closed Won", "closed-lost": "Closed Lost" };
  const idx = states.indexOf(current);
  return states.map((s, i) => ({ key: s, label: labels[s], done: i < idx, current: i === idx }));
}

function syncWorkOrderSituations(state: AppState, workOrderId: string, to: WorkOrderStatus, verification?: "pass" | "fail") {
  return state.situations.map((s) => {
    if (s.linkedWorkOrderId !== workOrderId) return s;
    // A composite situation (e.g. sit-hartley) can CITE a work order as one
    // of several verified sources without the work order BEING the
    // situation. Only situations whose entire nature is the work order
    // itself (category "work-order" / "inspection-followup") should have
    // their needsAttention/status/workflow driven by the work order's own
    // lifecycle — a composite situation's needsAttention/resolution stays
    // under the DM's explicit control (Resolve/Defer), never flipped as a
    // side effect of a linked fact changing. This is the concrete guard for
    // FF01_PRODUCT_MODEL.md's "assembled, not replaced" rule.
    if (s.category !== "work-order" && s.category !== "inspection-followup") return s;
    const resolved = to === "verified" || to === "closed";
    return {
      ...s,
      status: resolved ? "resolved" : s.status,
      needsAttention: to === "ready-to-verify",
      summary: verification === "fail" ? "Verification failed — sent back to vendor for repair." : s.summary,
      workflow: s.workflow ? { machine: "work-order", steps: workOrderStepStatusLocal(to) } : s.workflow
    };
  });
}

function syncTourSituations(state: AppState, tourId: string, to: Tour["status"]) {
  return state.situations.map((s) => {
    if (s.linkedTourId !== tourId) return s;
    const resolved = to === "closed-won" || to === "closed-lost";
    return {
      ...s,
      status: resolved ? "resolved" : s.status,
      needsAttention: to === "scheduled" || to === "outcome-recorded",
      workflow: s.workflow ? { machine: "tour", steps: tourStepStatusLocal(to) } : s.workflow
    };
  });
}
